import React, { useState, useRef, useEffect } from 'react';
import { useChatbot } from './useChatbot';
import './Chatbot.css';

// Sohbet, AYNI tarayıcı oturumu boyunca (yenileme + SPA gezinme dahil) korunur;
// tüm sekmeler kapanınca (oturum biter) sıfırlanır -> sessionStorage (localStorage DEĞİL).
const CHAT_STORE_KEY = 'sa-chat-msgs';
const GREETING_TEXT = 'Merhaba! Size Serdivan Belediyesi hakkında nasıl yardımcı olabilirim? Haberler, duyurular, etkinlikler veya hizmetler hakkında sorabilirsiniz.';
const loadStoredMessages = () => {
  try {
    const raw = sessionStorage.getItem(CHAT_STORE_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    if (Array.isArray(arr) && arr.length) {
      return arr.map((m) => ({ ...m, timestamp: m.timestamp ? new Date(m.timestamp) : new Date() }));
    }
  } catch { /* bozuk/erişilemez -> varsayılan */ }
  return [{ id: 1, text: GREETING_TEXT, isBot: true, timestamp: new Date() }];
};

const Chatbot = ({ isOpen, onClose, voiceMode = false, onVoiceState = null, heroSlotRef = null }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState(loadStoredMessages);
  const [isTyping, setIsTyping] = useState(false);
  // Düşünme süreci metinleri: kullanıcıya gerçekten onun için çalışıldığını hissettir.
  const THINKING_STEPS = [
    'Sizin için araştırıyorum…',
    'Bilgileri topluyorum…',
    'Kaynakları tarıyorum…',
    'Cevabınızı hazırlıyorum…',
  ];
  const [thinkingStep, setThinkingStep] = useState(0);
  useEffect(() => {
    if (!isTyping) return undefined;
    setThinkingStep(0);
    const iv = setInterval(() => setThinkingStep((s) => (s + 1) % THINKING_STEPS.length), 2000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const chatCtxRef = useRef(null);     // kalıcı AudioContext (her speak'te YENİ açma)
  const prevSrcRef = useRef(null);     // o an çalan AudioBufferSourceNode (stop için)
  const chatAnalyserRef = useRef(null);
  // Son mesaj id'si: geri yüklenen (önceki oturumdan) mesajlar otomatik seslendirilmesin.
  const lastSpokenRef = useRef(messages[messages.length - 1]?.id ?? 1);
  // Sesli okuma durumu: { id, status: 'loading' | 'playing' }
  const [audioState, setAudioState] = useState({ id: null, status: 'idle' });

  // Sohbeti oturum deposuna yaz (her mesaj değişiminde) -> yenileme/gezinmede korunur.
  useEffect(() => {
    try { sessionStorage.setItem(CHAT_STORE_KEY, JSON.stringify(messages)); } catch { /* yok */ }
  }, [messages]);

  const notifyVoice = (sp) => {
    if (onVoiceState) onVoiceState({ speaking: sp, analyser: chatAnalyserRef.current });
  };

  // Metni sesli okuma parçalarına böl: linkler/markdown/öneriler temizlenir,
  // cümle grupları ~180 karakterlik parçalara toplanır. Küçük parçalar = ilk ses
  // ÇOK daha erken başlar (CPU TTS tam metni beklemez) + sunucu cache'i cümle bazlı olur.
  const splitForSpeech = (raw) => {
    let t = raw || '';
    const idx = t.indexOf('Başka bir sorunuz var mı?');
    if (idx >= 0) t = t.slice(0, idx);
    t = t.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    t = t.replace(/\*\*/g, '');
    t = t.replace(/^\s*[-*•]\s+/gm, '');
    t = t.replace(/https?:\/\/\S+/g, '');
    // Kısaltmaları AÇ (bölmeden önce): "Mah." noktası cümle sonu sanılıp adres ortadan bölünmesin.
    t = t.replace(/\bMah\./gi, 'Mahallesi').replace(/\bCad\./gi, 'Caddesi')
      .replace(/\bSok\./gi, 'Sokak').replace(/\bBlv\./gi, 'Bulvarı')
      .replace(/\bNo\s*[:.]\s*/gi, 'numara ');
    t = t.replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim();
    if (!t) return [];
    // E-posta/alan adı noktalarını koru (cümle bölme "info@serdivan. bel. tr" diye parçalamasın)
    t = t.replace(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b|\b(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}\b/g,
      (m) => m.split('.').join('\u0001')
    );
    const parts = [];
    t.split('\n').forEach((line, li) => {
      let ln = line.trim();
      if (!ln) return;
      // Satır (liste maddesi) sonuna nokta: TTS maddeler arasında nefes alsın, birbirine girmesin.
      if (!/[.!?:]$/.test(ln)) ln += '.';
      (ln.match(/[^.!?]+[.!?]?/g) || []).forEach((s) => {
        const v = s.trim();
        if (v) parts.push({ li, v });
      });
    });
    const chunks = [];
    let buf = '';
    let bufLine = -1;
    // MIN_LEN: çok kısa parça (2-3 kelimelik liste maddesi) TTS'i tekrara/halüsinasyona
    // sokuyor -> en az bu uzunluğa ulaşana kadar biriktir. Yeterince uzun maddeler
    // (telefon/adres ~40+ karakter) yine ayrı kalır -> kekemelik geri gelmez.
    const MIN_LEN = 28;
    parts.forEach(({ li, v }) => {
      // İlk parça KÜÇÜK (~90) -> ilk ses çok daha erken başlar; sonrakiler 180.
      const limit = chunks.length === 0 ? 90 : 180;
      // Yeni parçaya geç: buf YETERİNCE UZUN ise VE (yeni satır VEYA limit aşılıyor).
      // buf kısaysa satır sınırı olsa bile birleştirmeye devam et (min uzunluğa ulaşsın).
      if (buf && buf.length >= MIN_LEN && (li !== bufLine || (buf.length + v.length + 1) > limit)) {
        chunks.push(buf); buf = v;
      } else {
        buf = buf ? buf + ' ' + v : v;
      }
      bufLine = li;
    });
    // Son parça çok kısaysa (örn. listenin son maddesi) önceki parçaya ekle -> kısa parça kalmasın.
    if (buf) {
      if (buf.length < MIN_LEN && chunks.length > 0) chunks[chunks.length - 1] += ' ' + buf;
      else chunks.push(buf);
    }
    // placeholder'ları noktaya geri çevir
    return chunks.map((c) => c.split('\u0001').join('.'));
  };

  const fetchTtsBuf = async (text) => {
    const resp = await fetch('/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!resp.ok) throw new Error('tts HTTP ' + resp.status);
    return resp.arrayBuffer();
  };

  // Aktif konuşma oturumu: artınca eski kuyruk kendini iptal eder.
  const speechSeqRef = useRef(0);
  const stopSpeech = () => {
    speechSeqRef.current += 1;
    try { prevSrcRef.current && prevSrcRef.current.stop(); } catch { /* zaten durmuş */ }
    prevSrcRef.current = null;
    setAudioState({ id: null, status: 'idle' });
    notifyVoice(false);
  };

  // Bir parçayı çal. CSP media-src'a takılmamak için blob:/MediaElement DEĞİL,
  // Web Audio (decodeAudioData + BufferSource) kullanılır; orb analyser'ı bu kaynağa bağlanır.
  const playChunk = (arrbuf, seq) => new Promise((resolve, reject) => {
    if (speechSeqRef.current !== seq) { resolve(false); return; }
    let ctx;
    try {
      ctx = chatCtxRef.current || (chatCtxRef.current = new (window.AudioContext || window.webkitAudioContext)());
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch (e) { reject(e); return; }
    ctx.decodeAudioData(arrbuf).then((audioBuf) => {
      if (speechSeqRef.current !== seq) { resolve(false); return; }
      try { prevSrcRef.current && prevSrcRef.current.stop(); } catch { /* yok */ }
      const src = ctx.createBufferSource();
      src.buffer = audioBuf;
      const an = ctx.createAnalyser();
      an.fftSize = 128;
      src.connect(an);
      an.connect(ctx.destination);
      prevSrcRef.current = src;
      chatAnalyserRef.current = an;
      src.onended = () => resolve(true);
      src.start(0);
      notifyVoice(true);
    }).catch(reject);
  });

  // Bir bot cevabını klonlanmış sesle seslendir: parça parça (pipeline) —
  // bir parça çalarken SIRADAKİ parça arka planda üretilir/indirilir.
  const speak = async (msgId, text) => {
    // Aynı mesaj çalıyorsa durdur (toggle)
    if (prevSrcRef.current && audioState.id === msgId && audioState.status === 'playing') {
      stopSpeech();
      return;
    }
    stopSpeech();
    const seq = speechSeqRef.current;
    const chunks = splitForSpeech(text);
    if (!chunks.length) return;
    setAudioState({ id: msgId, status: 'loading' });
    try {
      let nextBuf = fetchTtsBuf(chunks[0]);
      for (let i = 0; i < chunks.length; i += 1) {
        const buf = await nextBuf;
        if (speechSeqRef.current !== seq) return; // iptal edildi
        if (i + 1 < chunks.length) nextBuf = fetchTtsBuf(chunks[i + 1]); // prefetch
        if (i === 0) setAudioState({ id: msgId, status: 'playing' });
        const ok = await playChunk(buf, seq);
        if (!ok || speechSeqRef.current !== seq) return;
        notifyVoice(false); // parça arası: orb nefes molası
      }
      setAudioState({ id: null, status: 'idle' });
    } catch (err) {
      if (speechSeqRef.current !== seq) return;
      console.error('TTS hatası:', err);
      setAudioState({ id: null, status: 'idle' });
      notifyVoice(false);
    }
  };

  // Ses modunda: yeni bot cevabı gelince otomatik seslendir (ses dalgası konuşur).
  useEffect(() => {
    if (!voiceMode || !isOpen) return;
    const last = messages[messages.length - 1];
    if (last && last.isBot && last.id !== lastSpokenRef.current) {
      lastSpokenRef.current = last.id;
      speak(last.id, last.text);
    }
    // speak bilerek bağımlılığa eklenmedi (her render'da değişir); messages tetikleyicisi yeterli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, voiceMode, isOpen]);
  
  const { sendMessage, loading } = useChatbot({
    onResponse: (response) => {
      const newMessage = {
        id: Date.now(),
        text: response,
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, newMessage]);
      setIsTyping(false);
    }
  });

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when chatbot is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore body scroll when chatbot is closed
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  useEffect(() => {
    // Use setTimeout to ensure DOM is updated before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [messages, isTyping]);

  // Ana sayfa Swiper'ı (Mousewheel + touch/pointer) chatbot'un ATASI olduğundan,
  // chatbot içindeki kaydırma/dokunma event'leri yukarı sızıp slaytı değiştiriyor.
  // React synthetic handler'ı Swiper'ın native listener'ından SONRA çalıştığı için
  // geç kalır; bu yüzden native listener ile bubble'ı Swiper'a ulaşmadan durduruyoruz.
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = overlayRef.current;
    if (!el) return undefined;
    const stop = (e) => { e.stopPropagation(); };
    const opts = { passive: false };
    el.addEventListener('wheel', stop, opts);
    el.addEventListener('touchstart', stop, opts);
    el.addEventListener('touchmove', stop, opts);
    el.addEventListener('pointerdown', stop, opts);
    return () => {
      el.removeEventListener('wheel', stop, opts);
      el.removeEventListener('touchstart', stop, opts);
      el.removeEventListener('touchmove', stop, opts);
      el.removeEventListener('pointerdown', stop, opts);
    };
  }, [isOpen]);

  // Metni doğrudan gönder (öneri çipleri de bunu çağırır -> tıklayınca hemen sorulur).
  const submitText = async (text) => {
    const t = (text || '').trim();
    if (!t || loading) return;
    const userMessage = { id: Date.now(), text: t, isBot: false, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsTyping(true);
    setTimeout(() => { scrollToBottom(); }, 50);
    await sendMessage(t);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await submitText(message);
  };

  // Suggest common questions with more specific examples
  const suggestQuestions = () => {
    const suggestions = [
      "Son haberler neler?",
      "Bu hafta hangi etkinlikler var?",
      "Bugün etkinlik var mı?",
      "İmar durumu nasıl sorgulanır?",
      "Evlilik işlemleri için hangi belgeler gerekli?",
      "Online vergi ödeme nasıl yapılır?",
      "İş yeri açma ruhsatı için neler gerekli?",
      "SERBİM nasıl kullanılır?",
      "Belediye hizmetleri nelerdir?",
      "Belediye kuralları yönetmeliği nedir?",
      "Tadilat hangi saatlerde yapılabilir?",
      "Son mecliste ne karar alındı?",
      "Meclis kararlarını nasıl görebilirim?"
    ];
    
    return suggestions.map((suggestion, index) => (
      <button
        key={index}
        onClick={() => submitText(suggestion)}
        style={{
          background: '#f0f8ff',
          border: '1px solid #2196F3',
          borderRadius: '12px',
          padding: '6px 12px',
          margin: '4px',
          fontSize: '12px',
          color: '#2196F3',
          cursor: 'pointer'
        }}
      >
        {suggestion}
      </button>
    ));
  };

  const handleClose = () => {
    stopSpeech(); // kuyruk dahil tüm sesi iptal et
    try { chatCtxRef.current && chatCtxRef.current.suspend(); } catch { /* yok */ }
    onClose();
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Satır içi markdown: **kalın** -> <strong>, [metin](url) -> yeni sekmede link
  const renderInline = (line) => {
    const parts = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        let href = link[2].trim();
        // Güvenlik ağı: LLM bazen "http://haberler/x" gibi bozuk üretebiliyor -> normalize et.
        href = href.replace(
          /^https?:\/\/(haberler|etkinlik|etkinlik-takvimi|hizmetler|duyurular)(\/|$)/i,
          'https://serdivan.bel.tr/$1$2'
        );
        if (!/^(https?:\/\/|tel:|mailto:)/i.test(href) && !href.startsWith('/')) href = '/' + href;
        // tel:/mailto: aynı pencerede açılır (yeni sekme boş sayfa bırakır)
        const newTab = /^https?:\/\//i.test(href) || href.startsWith('/');
        return (
          <a
            key={i}
            href={href}
            className="chatbot-link"
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noopener noreferrer' : undefined}
          >
            {link[1]}
          </a>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  // Blok markdown: paragraf + madde listesi (-, *, • ile başlayan satırlar)
  const renderRichText = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const blocks = [];
    let list = null;
    lines.forEach((line) => {
      const bullet = line.match(/^([*\-•])\s+(.*)$/);
      if (bullet) {
        if (!list) list = [];
        list.push(bullet[2]);
      } else {
        if (list) { blocks.push({ type: 'ul', items: list }); list = null; }
        blocks.push({ type: 'p', text: line });
      }
    });
    if (list) blocks.push({ type: 'ul', items: list });

    return blocks.map((b, i) => (
      b.type === 'ul' ? (
        <ul key={i} className="chatbot-md-list">
          {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      ) : (
        <p key={i} className="chatbot-md-p">{renderInline(b.text)}</p>
      )
    ));
  };

  // Ana cevabı markdown olarak, sonundaki "Başka bir sorunuz var mı?" önerilerini çip olarak render et.
  const renderMessageWithSuggestions = (text) => {
    const marker = 'Başka bir sorunuz var mı?';
    const idx = text.indexOf(marker);
    const mainText = (idx >= 0 ? text.slice(0, idx) : text).trim();
    const suggPart = idx >= 0 ? text.slice(idx + marker.length) : '';
    const suggestions = suggPart
      .split(/\n|•/)
      .map(s => s.replace(/^[\s\-*•]+/, '').trim())
      // Öneri çipleri düz metindir: LLM yanlışlıkla markdown link/kalın koyarsa temizle
      // ("[Ad](url) hakkında..." ham görünmesin; input'a da temiz metin yazılsın)
      .map(s => s.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim())
      .filter(s => s.length > 3);

    return (
      <div>
        {renderRichText(mainText)}
        {suggestions.length > 0 && (
          <div className="chatbot-suggestions-block">
            <p className="chatbot-suggestions-title">{marker}</p>
            <div className="chatbot-suggestions-list">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => submitText(suggestion)}
                  className="chatbot-suggestion-chip"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`chatbot-overlay ${voiceMode ? 'chatbot-overlay-voice' : ''}`} ref={overlayRef} onClick={handleClose}>
      <div className={`chatbot-container ${voiceMode ? 'chatbot-container-voice' : ''}`} onClick={(e) => e.stopPropagation()}>
        {voiceMode ? (
          /* Küre Sahne: orb hero (orb, fixed katmandan bu slota pinlenir) */
          <div className={`chatbot-hero status-${audioState.status}`}>
            <button className="chatbot-close-hero" onClick={handleClose} aria-label="Kapat">✕</button>
            <div className="chatbot-orb-slot" ref={heroSlotRef} />
            <h3 className="chatbot-hero-title">Serdivan+ Asistan</h3>
            <div className="chatbot-hero-status" aria-live="polite">
              {audioState.status === 'loading' ? 'Hazırlanıyor…'
                : audioState.status === 'playing' ? 'Konuşuyor…'
                  : isTyping ? THINKING_STEPS[thinkingStep] : 'Dinliyorum'}
            </div>
            <div className="chatbot-hero-wave" aria-hidden="true">
              {Array.from({ length: 9 }).map((_, i) => <span key={i} />)}
            </div>
          </div>
        ) : (
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div>
                <h3>Serdivan+ Asistan</h3>
                <span className="chatbot-status">Çevrimiçi</span>
              </div>
            </div>
            <button className="chatbot-close" onClick={handleClose}>✕</button>
          </div>
        )}

        <div className="chatbot-messages" ref={messagesContainerRef}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`message ${msg.isBot ? 'bot-message' : 'user-message'}${msg.isBot && audioState.id === msg.id && audioState.status === 'playing' ? ' is-speaking' : ''}`}
            >
              <div className="message-content">
                {msg.isBot ? renderMessageWithSuggestions(msg.text) : <p>{msg.text}</p>}
                <div className="message-footer">
                  {msg.isBot && (
                    <button
                      type="button"
                      className="chatbot-speak-btn"
                      onClick={() => speak(msg.id, msg.text)}
                      title="Cevabı sesli dinle"
                      aria-label="Cevabı sesli dinle"
                    >
                      {audioState.id === msg.id && audioState.status === 'loading'
                        ? '⏳ Hazırlanıyor…'
                        : audioState.id === msg.id && audioState.status === 'playing'
                          ? '⏸ Durdur'
                          : '🔊 Dinle'}
                    </button>
                  )}
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
          
          {messages.length === 1 && (
            <div className="suggestions" style={{ padding: '10px', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                Örnek sorular:
              </p>
              {suggestQuestions()}
            </div>
          )}
          
          {isTyping && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="thinking-row">
                  <span className="thinking-label" aria-live="polite">{THINKING_STEPS[thinkingStep]}</span>
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <form className="chatbot-input-form" onSubmit={handleSubmit}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Mesajınızı yazın..."
            disabled={loading}
            className="chatbot-input"
            autoComplete="off"
          />
          <button 
            type="submit" 
            disabled={!message.trim() || loading}
            className="chatbot-send-btn"
          >
            📤
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chatbot;